import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
  AttendanceSheet,
  BOMActivity,
  Consumption,
  MRQ,
  MRQLine,
  Paginated,
  Project,
  ProjectBOMVersion,
  Site,
  Staff,
  StockBalance,
} from "../../api-models";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { PageHeader } from "../../components/PageHeader";
import { SiteSelect } from "../../components/SiteSelect";
import { useUser } from "../../context/UserContext";
import { activityLabel } from "../../helpers/activities";
import { api, errorMessage } from "../../helpers/api";
import { DT_FORMATS } from "../../helpers/constants";
import {
  hasAnyGrant,
  hasGrantInProject,
  hasProjectGrant,
  isModuleEnabled,
} from "../../helpers/permissions";
import { useSiteLocationId } from "../../helpers/queries";
import { useApi } from "../../helpers/useApi";

// The spreadsheet importer carries its own data grid and .xlsx parser - a large chunk no
// other page needs, so it only loads once someone actually starts an import.
const BOMImportDialog = lazy(() => import("./BOMImportDialog"));

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const { request, loading } = useApi();
  const [project, setProject] = useState<Project | null>(null);
  // Set when the project itself cannot be read, so the page says why instead of staying blank.
  const [loadError, setLoadError] = useState<{
    status?: number;
    message: string;
  } | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [bomVersions, setBomVersions] = useState<ProjectBOMVersion[]>([]);
  const [mrs, setMrs] = useState<MRQ[]>([]);
  const [stock, setStock] = useState<StockBalance[]>([]);
  const [consumptions, setConsumptions] = useState<Consumption[]>([]);
  const [attendanceSheets, setAttendanceSheets] = useState<AttendanceSheet[]>(
    [],
  );
  // Staff tab: who is posted at the project's sites on staffDate, one server page at a time
  // (the /staff page size is capped, and a project's crew can outgrow it).
  const [staffRows, setStaffRows] = useState<Staff[]>([]);
  const [staffCount, setStaffCount] = useState(0);
  const [staffFilter, setStaffFilter] = useState<{
    date: dayjs.Dayjs;
    siteId?: number;
    page: number;
  }>(() => ({ date: dayjs(), page: 1 }));
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [siteForm] = Form.useForm();
  const [attendanceForm] = Form.useForm();

  const activeTab = searchParams.get("tab") ?? "overview";
  // Every tab hangs off its own endpoint, so each one is fetched the first time it is
  // opened rather than all at once on mount - Site Stock in particular pulls a 500-row page
  // that is pure waste for someone who came to read the Overview. Sites and BOM versions
  // ride along with the project because the Overview itself shows the site count and the
  // active BOM, which also leaves those two tabs already populated.
  const [loadedTabs, setLoadedTabs] = useState<Record<string, boolean>>({});

  // Sites.Write and BOM.Write are project-level authority: the server gates them with
  // hasWriteOnProject, which only a global or PROJECT-scoped grant satisfies.
  const canWriteSites = hasProjectGrant(user, "Sites.Write", Number(id));
  const canWriteBom = hasProjectGrant(user, "BOM.Write", Number(id));
  // These three are checked per-site by the server, so a grant at any site of the project
  // is enough to show the tab. Attendance.Write in particular is site-only by design
  // (§31.27) and can never appear under a project key.
  const siteIds = useMemo(() => sites.map((s) => s.id), [sites]);
  // Each register behind a tab is read-gated on its own, and a role such as HR reaches the
  // project with Projects.Read but none of the material ones - a tab the server would
  // refuse is not shown. A grant at any site of the project is enough for the server.
  const canReadBom = hasGrantInProject(user, "BOM.Read", Number(id), siteIds);
  const canReadRequests = hasGrantInProject(
    user,
    "Requests.Read",
    Number(id),
    siteIds,
  );
  const canReadConsumption = hasGrantInProject(
    user,
    "Consumption.Read",
    Number(id),
    siteIds,
  );
  const canReadStock = hasGrantInProject(
    user,
    "Stock.Read",
    Number(id),
    siteIds,
  );
  const canWriteRequests = hasGrantInProject(
    user,
    "Requests.Write",
    Number(id),
    siteIds,
  );
  const canWriteConsumption = hasGrantInProject(
    user,
    "Consumption.Write",
    Number(id),
    siteIds,
  );
  const canWriteAdjustments = hasGrantInProject(
    user,
    "Adjustments.Write",
    Number(id),
    siteIds,
  );
  // A project with a single site can send the adjustment form straight to that site's
  // store; with several, the form's own picker has to settle it.
  const loneSiteLocationId = useSiteLocationId(
    sites.length === 1 ? sites[0].id : undefined,
  );
  // Attendance is a separately-quoted module (spec §28) - the tab only appears when it is
  // enabled and the user can read attendance somewhere; the server re-checks per site.
  const canReadAttendance =
    isModuleEnabled(user, "attendance") && hasAnyGrant(user, "Attendance.Read");
  // The server narrows a site-scoped reader to their own sites of the project.
  const canReadStaff = hasAnyGrant(user, "Staff.Read");
  const canWriteAttendance = hasGrantInProject(
    user,
    "Attendance.Write",
    Number(id),
    siteIds,
  );

  // Only the project and its sites decide whether the page can open. The BOM versions are
  // asked for only by someone who may read them, and a failure there costs the BOM line
  // and tab, not the whole page.
  async function loadBase() {
    const base = await Promise.all([
      api.get<Project>(`/projects/${id}`),
      api.get<Site[]>(`/projects/${id}/sites`),
    ]).catch((err) => {
      setLoadError({
        status: axios.isAxiosError(err) ? err.response?.status : undefined,
        message: errorMessage(err),
      });
      return null;
    });
    if (!base) return;
    const [p, s] = base;
    let versions: ProjectBOMVersion[] = [];
    if (
      hasGrantInProject(
        user,
        "BOM.Read",
        Number(id),
        s.data.map((site) => site.id),
      )
    ) {
      try {
        const b = await api.get<Paginated<ProjectBOMVersion>>(`/bom-versions`, {
          params: { projectId: id, pageSize: 20 },
        });
        versions = b.data.data;
      } catch (err) {
        message.error(errorMessage(err));
      }
    }
    setProject(p.data);
    setSites(s.data);
    setBomVersions(versions);
    setLoadedTabs((prev) => ({
      ...prev,
      overview: true,
      sites: true,
      bom: true,
    }));
  }

  // Marked loaded up front so a re-render while the request is in flight cannot fire it a
  // second time; the tab keeps its spinner until the rows land.
  async function loadTab(tab: string) {
    setLoadedTabs((prev) => ({ ...prev, [tab]: true }));
    if (tab === "requests" && canReadRequests) {
      const res = await api.get<Paginated<MRQ>>(`/mrqs`, {
        params: { projectId: id, pageSize: 20 },
      });
      setMrs(res.data.data);
    } else if (tab === "consumption" && canReadConsumption) {
      const res = await api.get<Paginated<Consumption>>(`/consumptions`, {
        params: { projectId: id, pageSize: 20 },
      });
      setConsumptions(res.data.data);
    } else if (tab === "site-stock" && canReadStock) {
      // /stock is global, so the project's balances have to be picked out by site here.
      // Quarantine is pulled in alongside the stores: damaged stock is the project's to
      // account for, and it is dispositioned from this very tab.
      const ofThisProject = new Set(siteIds);
      const res = await api.get<Paginated<StockBalance>>("/stock", {
        params: { pageSize: 500, type: "SITE,QUARANTINE" },
      });
      setStock(
        res.data.data.filter(
          (bal) =>
            bal.location?.siteId != null &&
            ofThisProject.has(bal.location.siteId),
        ),
      );
    } else if (tab === "staff" && canReadStaff) {
      await loadStaff(staffFilter);
    } else if (tab === "attendance" && canReadAttendance) {
      const res = await api.get<Paginated<AttendanceSheet>>(
        `/attendance-sheets`,
        { params: { projectId: id, pageSize: 20 } },
      );
      setAttendanceSheets(res.data.data);
    }
  }

  async function loadStaff(filter: typeof staffFilter) {
    setStaffFilter(filter);
    const res = await api.get<Paginated<Staff>>("/staff", {
      params: {
        projectId: id,
        siteId: filter.siteId,
        date: filter.date.format("YYYY-MM-DD"),
        page: filter.page,
        pageSize: 50,
      },
    });
    setStaffRows(res.data.data);
    setStaffCount(res.data.count);
  }

  async function loadSites() {
    const res = await api.get<Site[]>(`/projects/${id}/sites`);
    setSites(res.data);
  }

  useEffect(() => {
    setProject(null);
    setLoadError(null);
    setLoadedTabs({});
    // Wiped so a tab opened on the previous project does not flash its rows under the
    // spinner while this project's fetch is still out.
    setMrs([]);
    setConsumptions([]);
    setStock([]);
    setAttendanceSheets([]);
    setStaffRows([]);
    setStaffCount(0);
    setStaffFilter({ date: dayjs(), page: 1 });
    loadBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Waits on the project so the Site Stock filter has this project's sites to work with;
  // the effect re-runs once the base load lands.
  useEffect(() => {
    if (!project || loadedTabs[activeTab]) return;
    loadTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, activeTab, loadedTabs]);

  // The site code is immutable once created - it seeds the site's default stock location
  // and is referenced by documents, so only the name, address and status can be edited.
  async function handleSaveSite() {
    const values = await siteForm.validateFields();
    if (editingSite)
      await request({
        method: "PUT",
        url: `/sites/${editingSite.id}`,
        data: { ...editingSite, ...values },
      });
    else
      await request({
        method: "POST",
        url: "/sites",
        data: { ...values, projectId: Number(id) },
      });
    setSiteModalOpen(false);
    loadSites();
  }

  function openSiteModal(site: Site | null) {
    setEditingSite(site);
    siteForm.resetFields();
    if (site) siteForm.setFieldsValue(site);
    setSiteModalOpen(true);
  }

  // A Daily Attendance Sheet is opened per site+date (spec §28.4) - the project is implied
  // by the page, so only the site (restricted to this project's) and date are asked for.
  async function handleCreateAttendanceSheet() {
    const values = await attendanceForm.validateFields();
    const created = await request<AttendanceSheet>({
      method: "POST",
      url: "/attendance-sheets",
      data: {
        siteId: values.siteId,
        attendanceDate: values.attendanceDate.format("YYYY-MM-DD"),
      },
    });
    setAttendanceModalOpen(false);
    navigate(`/attendance/${created.id}`);
  }

  if (loadError)
    return (
      <Result
        status={
          loadError.status === 403
            ? "403"
            : loadError.status === 404
              ? "404"
              : "error"
        }
        title={
          loadError.status === 403
            ? "You cannot open this project"
            : loadError.status === 404
              ? "Project not found"
              : "The project could not be loaded"
        }
        subTitle={loadError.message}
        extra={
          <Button type="primary" onClick={() => navigate("/projects")}>
            Back to projects
          </Button>
        }
      />
    );
  if (!project)
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );

  const activeBom =
    bomVersions.find((v) => v.status === "APPROVED_ACTIVE") ?? bomVersions[0];

  const bomActions = canWriteBom && (
    <Space style={{ marginBottom: 12 }}>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => navigate(`/bom-versions/new?projectId=${id}`)}
      >
        Create BOM
      </Button>
      <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
        Import from Excel/CSV
      </Button>
    </Space>
  );

  return (
    <div>
      <PageHeader
        title={`${project.code} - ${project.name}`}
        subtitle={<DocumentStatusTag status={project.status} />}
      />
      <Tabs
        data-tour="project-tabs"
        activeKey={activeTab}
        onChange={(key) =>
          setSearchParams(key === "overview" ? {} : { tab: key }, {
            replace: true,
          })
        }
        items={[
          {
            key: "overview",
            label: "Overview",
            children: (
              <div>
                <p>
                  Status: <DocumentStatusTag status={project.status} />
                </p>
                <p>Sites: {sites.length}</p>
                {canReadBom && (
                  <p>
                    BOM:{" "}
                    {activeBom ? (
                      <a
                        onClick={() =>
                          navigate(`/bom-versions/${activeBom.id}`)
                        }
                      >
                        {activeBom.docNo} ({activeBom.status})
                      </a>
                    ) : (
                      "None yet"
                    )}
                  </p>
                )}
              </div>
            ),
          },
          {
            key: "sites",
            label: "Sites",
            children: (
              <div>
                {canWriteSites && (
                  <Button
                    icon={<PlusOutlined />}
                    style={{ marginBottom: 12 }}
                    onClick={() => openSiteModal(null)}
                  >
                    New site
                  </Button>
                )}
                <Table
                  rowKey="id"
                  dataSource={sites}
                  pagination={false}
                  columns={[
                    { title: "Code", dataIndex: "code" },
                    { title: "Name", dataIndex: "name" },
                    {
                      title: "Status",
                      dataIndex: "status",
                      render: (v) => <Tag>{v}</Tag>,
                    },
                    ...(canWriteSites
                      ? [
                          {
                            title: "",
                            width: 80,
                            render: (_: unknown, r: Site) => (
                              <Button
                                type="link"
                                onClick={() => openSiteModal(r)}
                              >
                                Edit
                              </Button>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            ),
          },
          ...(canReadBom
            ? [
                {
                  key: "bom",
                  label: "BOM",
                  children: (
                    <div>
                      {bomActions}
                      {bomVersions.length ? (
                        <Table
                          rowKey="id"
                          dataSource={bomVersions}
                          pagination={false}
                          onRow={(r) => ({
                            onClick: () => navigate(`/bom-versions/${r.id}`),
                            style: { cursor: "pointer" },
                          })}
                          columns={[
                            { title: "Doc No", dataIndex: "docNo" },
                            { title: "Version", dataIndex: "versionNo" },
                            { title: "Source", dataIndex: "sourceType" },
                            {
                              title: "Status",
                              dataIndex: "status",
                              render: (v) => <DocumentStatusTag status={v} />,
                            },
                          ]}
                        />
                      ) : (
                        <Empty description="No BOM yet" />
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          ...(canReadRequests
            ? [
                {
                  key: "requests",
                  label: "Requests",
                  children: (
                    <div>
                      {canWriteRequests && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          style={{ marginBottom: 12 }}
                          disabled={sites.length === 0}
                          title={
                            sites.length === 0
                              ? "Add a site to this project first"
                              : undefined
                          }
                          onClick={() =>
                            navigate(`/requests/new?projectId=${id}`)
                          }
                        >
                          New request
                        </Button>
                      )}
                      <Table
                        rowKey="id"
                        dataSource={mrs}
                        loading={!loadedTabs.requests}
                        pagination={false}
                        onRow={(r) => ({
                          onClick: () => navigate(`/requests/${r.id}`),
                          style: { cursor: "pointer" },
                        })}
                        columns={[
                          { title: "Doc No", dataIndex: "docNo" },
                          { title: "Site", render: (_, r) => r.site?.name },
                          {
                            title: "Created At",
                            render: (_, r) =>
                              dayjs(r.createdAt).format(DT_FORMATS.dateTime),
                          },
                          {
                            title: "Activities",
                            render: (_, r) => (
                              <ActivityPaths activities={r.activities} />
                            ),
                          },
                          {
                            title: "Items",
                            render: (_, r) => (
                              <MRQItemsSummary lines={r.lines} />
                            ),
                          },
                          {
                            title: "Needed by",
                            dataIndex: "neededBy",
                            render: (v) =>
                              v ? dayjs(v).format(DT_FORMATS.date) : "",
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            render: (v) => <DocumentStatusTag status={v} />,
                          },
                        ]}
                      />
                    </div>
                  ),
                },
              ]
            : []),
          ...(canReadConsumption
            ? [
                {
                  key: "consumption",
                  label: "Consumption",
                  children: (
                    <div>
                      {canWriteConsumption && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          style={{ marginBottom: 12 }}
                          disabled={sites.length === 0}
                          title={
                            sites.length === 0
                              ? "Add a site to this project first"
                              : undefined
                          }
                          onClick={() =>
                            navigate(`/consumption/new?projectId=${id}`)
                          }
                        >
                          Record consumption
                        </Button>
                      )}
                      <Table
                        rowKey="id"
                        dataSource={consumptions}
                        loading={!loadedTabs.consumption}
                        pagination={false}
                        onRow={(r) => ({
                          onClick: () => navigate(`/consumption/${r.id}`),
                          style: { cursor: "pointer" },
                        })}
                        columns={[
                          { title: "Doc No", dataIndex: "docNo" },
                          { title: "Site", render: (_, r) => r.site?.name },
                          {
                            title: "Created At",
                            render: (_, r) =>
                              dayjs(r.createdAt).format(DT_FORMATS.dateTime),
                          },
                          {
                            title: "Activities",
                            render: (_, r) => (
                              <ActivityPaths activities={r.activities} />
                            ),
                          },
                          {
                            title: "Consumed At",
                            dataIndex: "consumedOn",
                            render: (v) => dayjs(v).format(DT_FORMATS.date),
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            render: (v) => <DocumentStatusTag status={v} />,
                          },
                        ]}
                      />
                    </div>
                  ),
                },
              ]
            : []),
          ...(canReadStock
            ? [
                {
                  key: "site-stock",
                  label: "Site Stock",
                  children: (
                    <div>
                      {canWriteAdjustments && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          style={{ marginBottom: 12 }}
                          disabled={sites.length === 0}
                          title={
                            sites.length === 0
                              ? "Add a site to this project first"
                              : undefined
                          }
                          onClick={() =>
                            navigate(
                              loneSiteLocationId
                                ? `/adjustments/new?type=DAMAGE&locationId=${loneSiteLocationId}`
                                : "/adjustments/new?type=DAMAGE",
                            )
                          }
                        >
                          Record damage, wastage or loss
                        </Button>
                      )}
                      <Table
                        rowKey="id"
                        dataSource={stock}
                        loading={!loadedTabs["site-stock"]}
                        pagination={false}
                        columns={[
                          {
                            title: "Store",
                            render: (_, r) => (
                              <Space size={4}>
                                {r.location?.name}
                                {r.location?.type === "QUARANTINE" && (
                                  <Tag color="orange">Quarantine</Tag>
                                )}
                              </Space>
                            ),
                          },
                          { title: "Code", render: (_, r) => r.item?.code },
                          { title: "Item", render: (_, r) => r.item?.name },
                          {
                            title: "Available",
                            render: (_, r) => r.onHandQty - r.reservedQty,
                          },
                          { title: "Reserved", dataIndex: "reservedQty" },
                          ...(canWriteAdjustments
                            ? [
                                {
                                  title: "",
                                  // Quarantined stock needs a disposition to ever leave that
                                  // store; everything else is adjusted where it stands.
                                  render: (_: unknown, r: StockBalance) =>
                                    r.location?.type === "QUARANTINE" ? (
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          navigate(
                                            `/adjustments/new?type=QUARANTINE_RESTOCK&locationId=${r.locationId}`,
                                          )
                                        }
                                      >
                                        Disposition
                                      </Button>
                                    ) : (
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          navigate(
                                            `/adjustments/new?type=DAMAGE&locationId=${r.locationId}`,
                                          )
                                        }
                                      >
                                        Adjust
                                      </Button>
                                    ),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </div>
                  ),
                },
              ]
            : []),
          ...(canReadStaff
            ? [
                {
                  key: "staff",
                  label: "Staff",
                  children: (
                    <div data-tour="project-staff">
                      <Space style={{ marginBottom: 12 }}>
                        <DatePicker
                          allowClear={false}
                          value={staffFilter.date}
                          onChange={(d) =>
                            d && loadStaff({ ...staffFilter, date: d, page: 1 })
                          }
                        />
                        <SiteSelect
                          allowClear
                          autoSelectSingle={false}
                          placeholder="All sites"
                          style={{ width: 240 }}
                          options={sites.map((s) => ({
                            value: s.id,
                            label: s.name,
                          }))}
                          value={staffFilter.siteId}
                          onChange={(v) =>
                            loadStaff({ ...staffFilter, siteId: v, page: 1 })
                          }
                        />
                      </Space>
                      {loadedTabs.staff && !staffRows.length ? (
                        <Empty description="Nobody is posted to this project on that date" />
                      ) : (
                        <Table
                          rowKey="id"
                          dataSource={staffRows}
                          loading={!loadedTabs.staff}
                          pagination={{
                            current: staffFilter.page,
                            pageSize: 50,
                            total: staffCount,
                            showSizeChanger: false,
                            onChange: (page) =>
                              loadStaff({ ...staffFilter, page }),
                          }}
                          onRow={(r) => ({
                            onClick: () => navigate(`/staff/${r.id}`),
                            style: { cursor: "pointer" },
                          })}
                          columns={[
                            { title: "Staff Code", dataIndex: "staffCode" },
                            { title: "Name", dataIndex: "name" },
                            { title: "Designation", dataIndex: "designation" },
                            {
                              title: "Site",
                              render: (_, r) => r.assignments?.[0]?.site?.name,
                            },
                            {
                              title: "Posted from",
                              render: (_, r) =>
                                r.assignments?.[0]
                                  ? dayjs(r.assignments[0].fromDate).format(
                                      DT_FORMATS.date,
                                    )
                                  : "",
                            },
                            {
                              title: "Until",
                              render: (_, r) =>
                                r.assignments?.[0]?.toDate
                                  ? dayjs(r.assignments[0].toDate).format(
                                      DT_FORMATS.date,
                                    )
                                  : "Open-ended",
                            },
                          ]}
                        />
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          ...(canReadAttendance
            ? [
                {
                  key: "attendance",
                  label: "Attendance",
                  children: (
                    <div>
                      {canWriteAttendance && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          style={{ marginBottom: 12 }}
                          disabled={sites.length === 0}
                          title={
                            sites.length === 0
                              ? "Add a site to this project first"
                              : undefined
                          }
                          onClick={() => {
                            attendanceForm.resetFields();
                            setAttendanceModalOpen(true);
                          }}
                        >
                          New attendance sheet
                        </Button>
                      )}
                      {loadedTabs.attendance && !attendanceSheets.length ? (
                        <Empty description="No attendance sheets yet" />
                      ) : (
                        <Table
                          rowKey="id"
                          dataSource={attendanceSheets}
                          loading={!loadedTabs.attendance}
                          pagination={false}
                          onRow={(r) => ({
                            onClick: () => navigate(`/attendance/${r.id}`),
                            style: { cursor: "pointer" },
                          })}
                          columns={[
                            { title: "Doc No", dataIndex: "docNo" },
                            { title: "Site", render: (_, r) => r.site?.name },
                            {
                              title: "Date",
                              dataIndex: "attendanceDate",
                              render: (v) => dayjs(v).format(DT_FORMATS.date),
                            },
                            {
                              title: "Calendar",
                              dataIndex: "classification",
                              render: (v) => String(v).replaceAll("_", " "),
                            },
                            {
                              title: "Supervisor",
                              render: (_, r) => r.supervisor?.name ?? "",
                            },
                            {
                              title: "Status",
                              dataIndex: "status",
                              render: (v) => <DocumentStatusTag status={v} />,
                            },
                          ]}
                        />
                      )}
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal
        title={editingSite ? `Edit site ${editingSite.code}` : "New site"}
        open={siteModalOpen}
        onCancel={() => setSiteModalOpen(false)}
        onOk={handleSaveSite}
        confirmLoading={loading}
        destroyOnHidden
      >
        <Form form={siteForm} layout="vertical">
          {!editingSite && (
            <Form.Item name="code" label="Code" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          {editingSite && (
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "ACTIVE", label: "Active" },
                  { value: "CLOSED", label: "Closed" },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
      <Modal
        title="New attendance sheet"
        open={attendanceModalOpen}
        onCancel={() => setAttendanceModalOpen(false)}
        onOk={handleCreateAttendanceSheet}
        confirmLoading={loading}
        destroyOnHidden
      >
        <Form form={attendanceForm} layout="vertical">
          <Form.Item name="siteId" label="Site" rules={[{ required: true }]}>
            <SiteSelect
              showSearch
              optionFilterProp="label"
              options={sites.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
          <Form.Item
            name="attendanceDate"
            label="Attendance date"
            rules={[{ required: true }]}
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
      {importOpen && (
        <Suspense fallback={<Spin fullscreen />}>
          <BOMImportDialog
            projectId={Number(id)}
            onClose={() => setImportOpen(false)}
            onImported={(version) => navigate(`/bom-versions/${version.id}`)}
          />
        </Suspense>
      )}
    </div>
  );
}

// The work a request or consumption record was raised for, each activity shown at its full
// depth ("1 - Substructure > 1.2 - Excavation"). pathLabel is derived by the server for
// these list rows, which hand out an activity without the tree it hangs in; the bare label
// is the fallback for a payload that predates it.
function ActivityPaths({
  activities,
}: {
  activities?: { id: number; activity?: BOMActivity }[];
}) {
  const paths = (activities ?? [])
    .map((a) => a.activity?.pathLabel || activityLabel(a.activity))
    .filter(Boolean);
  if (!paths.length) return <>-</>;
  return (
    <Space direction="vertical" size={0}>
      {paths.map((p) => (
        <span key={p}>{p}</span>
      ))}
    </Space>
  );
}

// A request can carry dozens of lines, so the cell shows the first few item codes and
// hides the rest behind a count; the tooltip spells out code, name and requested qty for
// every line, which is what someone scanning the tab is actually trying to compare.
const ITEMS_SHOWN = 2;

function MRQItemsSummary({ lines }: { lines?: MRQLine[] }) {
  if (!lines?.length) return <>-</>;
  const codes = lines
    .slice(0, ITEMS_SHOWN)
    .map((l) => l.item?.code)
    .filter(Boolean)
    .join(", ");
  const hidden = lines.length - ITEMS_SHOWN;
  return (
    <Tooltip
      title={lines.map((l) => (
        <div key={l.id}>
          {l.item?.code} - {l.item?.name} × {l.requestedQty}
        </div>
      ))}
    >
      <span>
        {codes}
        {hidden > 0 ? ` +${hidden} more` : ""}
      </span>
    </Tooltip>
  );
}
